import { createContext, useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { authService, STORAGE_KEY } from "@/services/auth.service";

interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  roles: string[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => void;
  setAuthData: (token: string, userData: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(() => {
    // Initialize from localStorage
    const { user } = authService.getStoredAuth();
    return user;
  });

  const { data, isLoading } = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      const { token } = authService.getStoredAuth();
      if (!token) return null;

      try {
        const userData = await authService.getCurrentUser(token);
        return userData;
      } catch (error) {
        // Don't logout on network errors or temporary issues
        console.error("Error fetching user data:", error);
        // Return the stored user data as fallback
        const { user } = authService.getStoredAuth();
        return user;
      }
    },
    retry: 3, // Retry failed requests up to 3 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep data in cache for 30 minutes
  });

  useEffect(() => {
    if (data) {
      setUser(data);
    }
  }, [data]);

  const setAuthData = (token: string, userData: User) => {
    localStorage.setItem(STORAGE_KEY.TOKEN, token);
    localStorage.setItem(STORAGE_KEY.USER, JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        logout,
        setAuthData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};